/**
 * PALIM product galleries, like a social media carousel (styles in snippets/palim-style.liquid):
 * - a "1 / 3" counter pill on the photo and a row of dots under it that follow the photo shown
 * - the theme's thumbnail strip is hidden; its buttons are still used to change the photo on desktop
 * - arrows: the theme's own prev/next buttons scroll the photo slider, which is all phones need. On desktop, a
 *   thumbnail gallery shows one photo at a time and never scrolls, so there the arrows step to the previous/next
 *   thumbnail instead (looping), and stay enabled.
 */
(() => {
  const desktop = window.matchMedia('(min-width: 750px)');

  /** @param {HTMLElement} gallery - a <media-gallery> */
  const thumbs = (gallery) => [...gallery.querySelectorAll('[id^="GalleryThumbnails"] [data-target]')];
  const slides = (gallery) => [...gallery.querySelectorAll('[id^="Slider-Gallery"] > .product__media-item')];
  const isThumbLayout = (gallery) => desktop.matches && gallery.dataset.desktopLayout === 'thumbnail_slider';

  const stepThumbnail = (gallery, direction) => {
    const items = thumbs(gallery);
    if (items.length < 2) return false;
    const current = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
    const next = items[(current + direction + items.length) % items.length];
    next.querySelector('button')?.click();
    return true;
  };

  /** The photo shown now: the active slide on desktop, the scroll position on phones. */
  const currentIndex = (gallery) => {
    const list = slides(gallery);
    if (isThumbLayout(gallery)) return Math.max(0, list.findIndex((slide) => slide.classList.contains('is-active')));
    const slider = gallery.querySelector('[id^="Slider-Gallery"]');
    if (!slider || !list.length) return 0;
    const width = list[0].getBoundingClientRect().width || 1;
    return Math.min(list.length - 1, Math.max(0, Math.round(slider.scrollLeft / width)));
  };

  const goTo = (gallery, index) => {
    if (isThumbLayout(gallery)) {
      thumbs(gallery)[index]?.querySelector('button')?.click();
      return;
    }
    const slider = gallery.querySelector('[id^="Slider-Gallery"]');
    const target = slides(gallery)[index];
    if (slider && target) slider.scrollTo({ left: target.offsetLeft - slider.offsetLeft, behavior: 'smooth' });
  };

  const update = (gallery) => {
    const dots = gallery.querySelector('.palim-dots');
    if (!dots) return;
    const index = currentIndex(gallery);
    [...dots.children].forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    const counter = gallery.querySelector('.slider-counter--current');
    if (counter) counter.textContent = String(index + 1);
  };

  const build = (gallery) => {
    if (gallery.querySelector('.palim-dots')) return;
    const list = slides(gallery);
    if (list.length < 2) return;
    gallery.classList.add('palim-carousel');
    const dots = document.createElement('div');
    dots.className = 'palim-dots';
    list.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'palim-dots__dot';
      dot.setAttribute('aria-label', `Photo ${i + 1} of ${list.length}`);
      dot.addEventListener('click', () => goTo(gallery, i));
      dots.append(dot);
    });
    const viewer = gallery.querySelector('product-gallery');
    const anchor = viewer?.closest('use-animate') || viewer;
    anchor?.after(dots);
    const total = gallery.querySelector('.slider-counter--total');
    if (total) total.textContent = String(list.length);

    new MutationObserver(() => update(gallery)).observe(gallery, { attributes: true, subtree: true, attributeFilter: ['class'] });
    // Phones: follow the swipe while it moves, and settle on the final photo once it stops (smooth scrolling
    // keeps moving after the last scroll event on some browsers).
    const slider = gallery.querySelector('[id^="Slider-Gallery"]');
    let settle;
    slider?.addEventListener('scroll', () => {
      update(gallery);
      clearTimeout(settle);
      settle = setTimeout(() => update(gallery), 150);
    }, { passive: true });
    slider?.addEventListener('scrollend', () => update(gallery));
    update(gallery);
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('product-gallery .slider-buttons .slider-button');
    if (!button) return;
    const gallery = button.closest('media-gallery');
    if (!gallery || !isThumbLayout(gallery)) return;
    if (stepThumbnail(gallery, button.name === 'next' ? 1 : -1)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  // The theme disables the buttons from the (unused) scroll position on desktop; keep them usable there.
  const enable = () => {
    for (const gallery of document.querySelectorAll('media-gallery[data-desktop-layout="thumbnail_slider"]')) {
      if (!isThumbLayout(gallery) || thumbs(gallery).length < 2) continue;
      for (const button of gallery.querySelectorAll('product-gallery .slider-buttons .slider-button')) {
        button.removeAttribute('disabled');
      }
    }
  };
  const observer = new MutationObserver(enable);
  const start = () => {
    for (const gallery of document.querySelectorAll('media-gallery')) {
      build(gallery);
      if (gallery.dataset.desktopLayout === 'thumbnail_slider') {
        observer.observe(gallery, { attributes: true, subtree: true, attributeFilter: ['disabled'] });
      }
    }
    enable();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  desktop.addEventListener('change', () => {
    enable();
    document.querySelectorAll('media-gallery').forEach(update);
  });
})();
